import { Button, buttonClass } from '@league/ui';

/**
 * "PRINT TABLE TENTS" — owner, 2026-10-16: this was a link above two radio buttons (quarter-sheet
 * cards or folded tents), beside "Print score sheet" and "Show QR code" as buttons. Choosing a radio
 * did nothing visible, so it read as broken, and a tent already carries the table number, so the
 * cards had no job. Now one button like its neighbours, a plain link to the PDF in a new tab, like
 * the score sheet. Never saves seating.
 */
const BUTTON = buttonClass('secondary');

export function PrintTableNumbers({ sessionId, hasSavedTables, tableNumbersChanged }: {
  sessionId: string;
  hasSavedTables: boolean;
  tableNumbersChanged: boolean;
}) {
  const help = !hasSavedTables ? 'Save seating with players at a table before printing.' : tableNumbersChanged ? 'Save changes to print the updated table numbers.' : null;
  const href = `/api/sessions/${encodeURIComponent(sessionId)}/table-cards.pdf?format=folded`;
  return <>
    {hasSavedTables
      ? <a href={href} target="_blank" rel="noopener" aria-describedby={help ? 'table-number-print-help' : undefined} className={BUTTON}>Print table tents</a>
      : <Button variant="secondary" disabled aria-describedby="table-number-print-help">Print table tents</Button>}
    {help ? <p id="table-number-print-help" className="basis-full text-[13px] text-ink-2">{help}</p> : null}
  </>;
}

/**
 * "PRINT NAME CARDS" — owner, 2026-10-25: the cards existed but were reachable only from a dashboard
 * recommendation. Shown only when the server says the saved seating will print, so it is never a
 * button that answers with an error; the board hides it while there are unsaved changes, since the
 * cards print what is saved.
 */
export function PrintNameCards({ sessionId }: { sessionId: string }) {
  return <a href={`/api/sessions/${encodeURIComponent(sessionId)}/name-tents.pdf`} target="_blank" rel="noopener" className={BUTTON}>Print name cards</a>;
}

/**
 * The league's tents, from the Seating tab — owner, 2026-10-16: the score sheet prints from the Scores
 * tab without opening a session, and tents had to be fetched from inside one. Table numbers do not
 * change between weeks, so one set serves the league: as many as its busiest session, or its
 * capacity before anything is seated.
 */
export function PrintLeagueTableTents({ leagueId }: { leagueId: string }) {
  return <a href={`/api/leagues/${encodeURIComponent(leagueId)}/table-cards.pdf`} target="_blank" rel="noopener" className={BUTTON}>Print table tents</a>;
}

/** Every player at every table (Courtney, 2026-10-31), beside the tents those numbers match. */
export function PrintNameCardsForEveryTable({ leagueId }: { leagueId: string }) {
  return <a href={`/api/leagues/${encodeURIComponent(leagueId)}/name-cards-every-table.pdf`} target="_blank" rel="noopener" className={BUTTON}>Print name cards for every table</a>;
}
